import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import "../css/home.css";

const features = [
  {
    n: "01",
    title: "Exceptional Rigor",
    body: "We check every proof manually before starting the run. No blurred lines, no registration errors, just flawless ink delivery.",
  },
  {
    n: "02",
    title: "Nationwide Delivery",
    body: "We route completed runs securely to businesses across Nigeria. Deadlines respected, always.",
  },
  {
    n: "03",
    title: "Generational Craft",
    body: "Our pressmen bring decades of cumulative experience in heavy sheet-fed presses and light digital systems alike.",
  },
];

function PrintSheet() {
  const stageRef = useRef(null);

  const sheetRef = useRef(null);
  const shadowRef = useRef(null);

  const blueRef = useRef(null);
  const yellowRef = useRef(null);
  const magentaRef = useRef(null);
  const artworkRef = useRef(null);

  const crosshairRef = useRef(null);
  const crosshairInnerRef = useRef(null);

  const marksRef = useRef([]);

  const target = useRef({
    x: 0,
    y: 0,
  });

  const current = useRef({
    x: 0,
    y: 0,
  });

  useEffect(() => {
    let frame;

    const animate = (time) => {
      const t = time * 0.001;

      /*
       * Smooth mouse movement.
       */
      current.current.x +=
        (target.current.x - current.current.x) * 0.08;

      current.current.y +=
        (target.current.y - current.current.y) * 0.08;

      const x = current.current.x;
      const y = current.current.y;

      /*
       * =====================================
       * PAPER TILT
       * =====================================
       */

      const rotateX = -y * 7;
      const rotateY = x * 9;

      const floatY = Math.sin(t * 0.8) * 2;

      if (sheetRef.current) {
        sheetRef.current.style.transform = `
          translate(-50%, -50%)
          translateY(${floatY}px)
          perspective(1000px)
          rotateX(${rotateX}deg)
          rotateY(${rotateY}deg)
        `;
      }

      /*
       * =====================================
       * SHADOW
       * =====================================
       */

      if (shadowRef.current) {
        const shadowX = 18 - x * 18;
        const shadowY = 18 + y * 14;

        shadowRef.current.style.transform = `
          translate(-50%, -47%)
          translate(${shadowX}px, ${shadowY}px)
        `;

        shadowRef.current.style.opacity =
          `${0.12 + Math.abs(x + y) * 0.035}`;
      }

      /*
       * =====================================
       * CYAN LAYER
       * =====================================
       */

      if (blueRef.current) {
        blueRef.current.style.transform = `
          translate(${x * 5}px, ${y * 4}px)
          rotate(7deg)
        `;
      }

      /*
       * =====================================
       * YELLOW LAYER
       * =====================================
       */

      if (yellowRef.current) {
        yellowRef.current.style.transform = `
          translate(${x * -9}px, ${y * -7}px)
          rotate(-9deg)
        `;
      }

      /*
       * =====================================
       * MAGENTA LAYER
       * =====================================
       */

      if (magentaRef.current) {
        magentaRef.current.style.transform = `
          translate(${x * 13}px, ${y * 10}px)
          rotate(-5deg)
        `;
      }

      /*
       * =====================================
       * ARTWORK PARALLAX
       * =====================================
       */

      if (artworkRef.current) {
        artworkRef.current.style.transform = `
          translate(${x * 6}px, ${y * 5}px)
        `;
      }

      /*
       * =====================================
       * REGISTRATION MARKS
       * =====================================
       */

      marksRef.current.forEach((mark, index) => {
        if (!mark) return;

        const depth = index % 2 === 0 ? 0.8 : -0.8;

        mark.style.transform = `
          translate(
            ${x * depth * 4}px,
            ${y * depth * 4}px
          )
        `;
      });

      /*
       * =====================================
       * CROSSHAIR LAG
       * =====================================
       *
       * Crosshair deliberately follows the
       * cursor with a little physical lag.
       */

      if (crosshairRef.current) {
        const rect =
          stageRef.current.getBoundingClientRect();

        const targetX =
          rect.width / 2 +
          x * (rect.width / 2);

        const targetY =
          rect.height / 2 +
          y * (rect.height / 2);

        const currentX =
          parseFloat(
            crosshairRef.current.dataset.x || rect.width / 2
          );

        const currentY =
          parseFloat(
            crosshairRef.current.dataset.y || rect.height / 2
          );

        const nextX =
          currentX + (targetX - currentX) * 0.16;

        const nextY =
          currentY + (targetY - currentY) * 0.16;

        crosshairRef.current.dataset.x = nextX;
        crosshairRef.current.dataset.y = nextY;

        crosshairRef.current.style.left = `${nextX}px`;
        crosshairRef.current.style.top = `${nextY}px`;
      }

      /*
       * Tiny crosshair center movement.
       */

      if (crosshairInnerRef.current) {
        crosshairInnerRef.current.style.transform = `
          translate(-50%, -50%)
          rotate(${t * 8}deg)
        `;
      }

      frame = requestAnimationFrame(animate);
    };

    frame = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(frame);
    };
  }, []);

  function handleMouseMove(event) {
    const stage = stageRef.current;

    if (!stage) return;

    const rect = stage.getBoundingClientRect();

    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    const normalizedX =
      (mouseX / rect.width - 0.5) * 2;

    const normalizedY =
      (mouseY / rect.height - 0.5) * 2;

    target.current = {
      x: normalizedX,
      y: normalizedY,
    };

    if (crosshairRef.current) {
      crosshairRef.current.classList.add("is-visible");
    }
  }

  function handleMouseLeave() {
    target.current = {
      x: 0,
      y: 0,
    };

    if (crosshairRef.current) {
      crosshairRef.current.classList.remove("is-visible");
    }
  }

  return (
    <div
      ref={stageRef}
      className="print-stage"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* =================================
          BACKGROUND
      ================================= */}

      <div className="print-stage-grid" />

      <div className="stage-glow" />

      {/* =================================
          REGISTRATION MARKS
      ================================= */}

      <div
        ref={(el) => {
          marksRef.current[0] = el;
        }}
        className="print-registration registration-top-left"
      />

      <div
        ref={(el) => {
          marksRef.current[1] = el;
        }}
        className="print-registration registration-top-right"
      />

      <div
        ref={(el) => {
          marksRef.current[2] = el;
        }}
        className="print-registration registration-bottom-left"
      />

      <div
        ref={(el) => {
          marksRef.current[3] = el;
        }}
        className="print-registration registration-bottom-right"
      />

      {/* =================================
          PAPER SHADOW
      ================================= */}

      <div
        ref={shadowRef}
        className="paper-shadow"
      />

      {/* =================================
          MAIN PAPER
      ================================= */}

      <div
        ref={sheetRef}
        className="print-sheet"
      >
        {/* Paper texture */}
        <div className="sheet-texture" />

        {/* Top metadata */}
        <div className="sheet-topline">
          <span>IGC / 2018</span>
          <span>PRINT / 001</span>
        </div>

        {/* CMYK circles */}
        <div className="sheet-cmyk">
          <span>C</span>
          <span>M</span>
          <span>Y</span>
          <span>K</span>
        </div>

        {/* Main typography */}
        <div className="sheet-hero-type">
          <span>PRINT</span>
          <span>WITH</span>
          <span>PURPOSE.</span>
        </div>

        {/* =================================
            PRINT LAYERS
        ================================= */}

        <div
          ref={blueRef}
          className="sheet-blue-block"
        />

        <div
          ref={yellowRef}
          className="sheet-yellow-block"
        />

        <div
          ref={magentaRef}
          className="sheet-magenta-block"
        />

        {/* =================================
            ABSTRACT ARTWORK
        ================================= */}

        <div
          ref={artworkRef}
          className="sheet-image"
        >
          <div className="image-circle" />

          <div className="image-line line-one" />
          <div className="image-line line-two" />
          <div className="image-line line-three" />

          <div className="image-small-type">
            COLOR
          </div>
        </div>

        {/* IGC mark */}
        <div className="sheet-mark">
          IGC
        </div>

        {/* Bottom metadata */}
        <div className="sheet-bottom">
          <span>QUALITY PRINTING</span>
          <span>RIVERS STATE / NIGERIA</span>
        </div>
      </div>

      {/* =================================
          MOUSE LIGHT
      ================================= */}

      <div className="mouse-light" />

      {/* =================================
          MOUSE CROSSHAIR
      ================================= */}

      <div
        ref={crosshairRef}
        className="print-crosshair"
      >
        <span className="crosshair-horizontal" />
        <span className="crosshair-vertical" />

        <span
          ref={crosshairInnerRef}
          className="crosshair-center"
        />

        <span className="crosshair-label">
          REG
        </span>
      </div>

      {/* =================================
          STAGE INFORMATION
      ================================= */}

      <div className="print-caption">
        <span className="caption-line" />
        <span>
          OFFSET / DIGITAL / LARGE FORMAT
        </span>
      </div>

      <div className="print-number">
        01
      </div>

      <div className="print-corner-label">
        MOVE / EXPLORE
      </div>
    </div>
  );
}

export default function Home() {
  return (
    <div className="home">

      {/* =================================
          HERO
      ================================= */}

      <section className="hero">

        <div className="wrap hero-inner">

          <div className="hero-text-col">

            <div className="hero-title-group">

              <span className="hero-tag">
                <span className="pulse" />
                EST. 2018
              </span>

              <h1 className="hero-title">
                Quality, reliable printing for
                Rivers State businesses
              </h1>

            </div>

            <p className="hero-sub">
              We deliver pristine paper stocks,
              rich ink color-matching, and crisp
              custom finishes. Family-owned and
              operated, printing for businesses
              across Nigeria since 2018.
            </p>

            <div className="hero-ctas">

              <Link
                to="/quote"
                className="btn btn-primary"
              >
                Start a Print Job
              </Link>

              <Link
                to="/services"
                className="btn btn-ghost"
              >
                See Services
              </Link>

            </div>

          </div>

          <div className="hero-image-col">
            <PrintSheet />
          </div>

        </div>

      </section>

      {/* =================================
          FEATURES
      ================================= */}

      <section className="features-section">

        <div className="wrap">

          <div className="section-heading">

            <span className="eyebrow">
              <span className="eyebrow-dot" />
              Our Edge
            </span>

            <h2 className="section-title">
              Why corporate teams and creatives
              choose IGC
            </h2>

          </div>

          <div className="features-row">

            {features.map((feature) => (
              <div
                className="feature-card"
                key={feature.n}
              >
                <p className="feature-n">
                  {feature.n}
                </p>

                <h3 className="feature-title">
                  {feature.title}
                </h3>

                <p className="feature-body">
                  {feature.body}
                </p>
              </div>
            ))}

          </div>

        </div>

      </section>

      {/* =================================
          CTA
      ================================= */}

      <section className="cta-banner">

        <div className="wrap cta-inner">

          <h2 className="cta-title">
            Let's make your next run pristine
          </h2>

          <p className="cta-sub">
            Contact us today for custom stock
            pairing, direct factory quotes, and
            express press scheduling.
          </p>

          <div className="cta-ctas">

            <Link
              to="/quote"
              className="btn btn-primary"
            >
              Start a Print Job
            </Link>

            <Link
              to="/contact"
              className="btn btn-ghost"
            >
              Contact Our Office
            </Link>

          </div>

        </div>

      </section>

    </div>
  );
}